import { Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useCampaigns } from "api/campaigns";
import { getBackendErrors, getErrorMessage } from "api/errors";
import {
  hasAnyAccessToResource,
  hasModifyAccessToResource,
  hasViewAccessToResource,
} from "api/general";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { CustomAttributesModal } from "components/Layout/Transaction/SingleTransaction/CustomAttributesModal";
import { Modals } from "components/Layout/Transaction/SingleTransaction/SingleTransaction";
import { StatusValue } from "components/Table/StatusValue";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CampaignTriggers } from "types/campaigns/";
import { backendErrors } from "utilities/backendErrors";
import { links } from "utilities/links";
import { KeyValuePair } from "utilities/objects";
import { CampaignDetails } from "./CampaignDetails";
import { CampaignOverview } from "./CampaignOverview";
import { CampaignStatus } from "./CampaignStatus";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      display: "inline-block",
      fontSize: 18,
      paddingBottom: theme.spacing(5),
    },
    sectionHeader: {
      fontSize: 18,
      paddingBottom: theme.spacing(12),
    },
    gridContainer: {
      margin: theme.spacing(12, 0),
    },
    gridChild: {
      display: "flex",
      flexDirection: "column",
    },
  }),
);

export const SingleCampaign = () => {
  const [isUpdatingCampaign, setIsUpdatingCampaign] = useState(false);
  const classes = useStyles();
  const navigate = useNavigate();
  const contextMenu = useContextMenu();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { id: campaignId } = useParams<{ id: string }>();
  const hasViewCampaignPermission = hasViewAccessToResource("CAMPAIGN");
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const hasModifyCampaignPermission = hasModifyAccessToResource("CAMPAIGN");
  const { useCampaignData, deleteCampaign, updateCampaign } = useCampaigns();
  const { data: campaignData } = useCampaignData(campaignId as string);

  const [open, setOpen] = useState(false);
  const [selectedModal, setSelectedModal] = useState<{
    transaction: string;
    mode: number;
  }>();
  const [customAttributes, setCustomAttributes] = useState<KeyValuePair[]>();
  const [deleteModal, setDeleteModal] = useState({
    campaignId: "",
    isOpen: false,
  });
  const [errors, setErrors] = useState<any>();

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "campaigns" }),
      path: links.campaignsList,
    },
  };

  const submitDeleteCampaign = useCallback(
    async (campaignId: string) => {
      try {
        const response = await deleteCampaign(campaignId);

        if (response) {
          setDeleteModal({ campaignId: "", isOpen: false });
          notify(message({ id: "campaignSuccessfullyDeleted" }));
          navigate(links.campaignsList);
        }
      } catch (error) {
        notify(getErrorMessage(error));
        setErrors(backendErrors(getBackendErrors(error)));
      }
    },
    [message, notify, deleteCampaign, navigate],
  );

  const toggleCampaignStatus = async () => {
    setIsUpdatingCampaign(true);
    try {
      await updateCampaign(campaignData?.campaignId as string, {
        active: !campaignData?.active,
      });
      notify(
        message({
          id: !campaignData?.active
            ? "campaignActivated"
            : "campaignDeactivated",
        }),
      );
    } catch (exception) {
      const backendErrors = getBackendErrors(exception);

      if (backendErrors) {
        notify(
          backendErrors.length > 0
            ? backendErrors[0].message
            : getErrorMessage(exception),
        );
      }
    }

    setIsUpdatingCampaign(false);
  };

  return (
    <>
      <TopHeader
        title={campaignData ? campaignData?.name : ""}
        clearLink={links.campaignsList}
        breadcrumbs={breadcrumbs}
        badge={<StatusValue status={!!campaignData?.active} />}
      />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderMenuButton
            text={message({ id: "actions" })}
            menuItems={[
              {
                text: message({
                  id: campaignData?.active
                    ? "deactivateCampaign"
                    : "activateCampaign",
                }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  toggleCampaignStatus();
                },
                hideCondition: !hasModifyCampaignPermission,
                disableCondition: isUpdatingCampaign,
              },
              {
                text: message({ id: "duplicateCampaign" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  navigate(links.addCampaign, {
                    state: {
                      campaignToDuplicate: campaignData?.campaignId,
                    },
                  });
                },
                hideCondition:
                  hasViewCampaignPermission || !hasAnyLanguagePermission,
              },
              {
                text: message({ id: "editCampaign" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  navigate(
                    links.editCampaign(campaignData?.campaignId as string),
                  );
                },
                hideCondition:
                  hasViewCampaignPermission || !hasAnyLanguagePermission,
              },
              {
                text: message({ id: "deleteCampaign" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setDeleteModal({
                    campaignId: campaignId as string,
                    isOpen: true,
                  });
                },
              },
              {
                text: message({ id: "showRedemptionCodes" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  navigate(
                    links.redemptionCodeList(
                      campaignData?.campaignId as string,
                    ),
                  );
                },
                hideCondition:
                  campaignData?.trigger !==
                  CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE,
              },
            ]}
            data-test-id="single-campaign"
          />
        </SubheaderWrapper>
      </Subheader>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <CampaignOverview campaignId={campaignId} title="campaignOverview" />
        </Grid>
      </Grid>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span
            className={classes.sectionHeader}
            data-test-id="single-campaign-campaign-status-header"
          >
            {message({ id: "campaignStatus" })}
          </span>
          <CampaignStatus campaignData={campaignData} />
        </Grid>
      </Grid>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span
            className={classes.sectionHeader}
            data-test-id="single-campaign-campaign-details-header"
          >
            {message({ id: "campaignDetails" })}
          </span>
          {campaignData && (
            <CampaignDetails
              campaignData={campaignData}
              openCustomAttributesModal={() => {
                setSelectedModal({
                  transaction: campaignData?.campaignId!,
                  mode: Modals.CustomAttributes,
                });
                setCustomAttributes(campaignData?.labels);
                setOpen(true);
              }}
            />
          )}
        </Grid>
      </Grid>
      {selectedModal?.mode === Modals.CustomAttributes && (
        <CustomAttributesModal
          open={open}
          close={() => {
            setOpen(false);
            setCustomAttributes(undefined);
          }}
          customAttributes={customAttributes}
        />
      )}
      {deleteModal.campaignId && (
        <ConfirmationModal
          action={() => submitDeleteCampaign(deleteModal.campaignId)}
          close={() => {
            setDeleteModal({ campaignId: "", isOpen: false });
            setErrors(null);
          }}
          open={deleteModal.isOpen}
          header="deleteCampaignQuestion"
          info="youCantUndoThisAction"
          submitText="yesDelete"
          cancelText="cancel"
          errorInfo={errors?.["campaignId"]}
          data-test-id="delete-campaign"
          modalWidth={545}
        />
      )}
    </>
  );
};
